import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// The workspace's own product catalog and the warehouses holding the stock.
// This is not a Meta commerce catalog — nothing here is published to WhatsApp's
// shop. It exists so customers can ask for items and be sent a quotation.

const TABS = ['Products', 'Warehouses'];

const money = (amount, currency = 'USD') => `${currency} ${Number(amount || 0).toFixed(2)}`;

export default function Catalog() {
  const { user } = useAuth();
  const isOwner = user?.team_role !== 'agent';
  const [tab, setTab] = useState('Products');

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Catalog</h1>
      </div>
      <p className="page-desc">
        What you sell, and how much of it sits where. Customers can ask for these items on WhatsApp
        and get a quotation back.
      </p>

      <div className="tabs" role="tablist">
        {TABS.map(t => (
          <button key={t} role="tab" aria-selected={tab === t}
            className={'btn-tab' + (tab === t ? ' active' : '')}
            onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {tab === 'Products' && <ProductsTab />}
      {tab === 'Warehouses' && <WarehousesTab isOwner={isOwner} />}
    </div>
  );
}

// ── products ─────────────────────────────────────────────────────────────────

function ProductsTab() {
  const [data, setData] = useState({ products: [], categories: [], warehouses: [], total: 0, pages: 1 });
  const [filters, setFilters] = useState({ q: '', category: '', warehouse: '', low_stock: '', active: '' });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // product object or {} for new
  const [importing, setImporting] = useState(false);
  const [moving, setMoving] = useState(null); // product to move stock for

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      for (const [key, value] of Object.entries(filters)) if (value) params[key] = value;
      const r = await api.get('/products', { params });
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not load the catalog');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const remove = async (product) => {
    if (!window.confirm(`Delete ${product.name}? Quotations already raised keep their own copy of it.`)) return;
    try {
      await api.delete(`/products/${product._id}`);
      toast.success('Product deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get('/products/csv-template', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products_sample.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Could not download the template'); }
  };

  return (
    <div className="card" style={{ padding: 20 }}>
      <div className="card-header">
        <div className="card-title">
          {data.total} product{data.total === 1 ? '' : 's'}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost btn-sm" onClick={downloadTemplate}>⬇ Sample CSV</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setImporting(true)}>📄 Import CSV</button>
          <button className="btn btn-primary btn-sm" onClick={() => setEditing({})}>+ New product</button>
        </div>
      </div>

      {!data.warehouses.length && (
        <div className="alert alert-info">
          <div>
            <div className="alert-title">Add a warehouse first</div>
            You can create products now, but stock has to live somewhere before you can record
            quantities or quote from a specific branch.
          </div>
        </div>
      )}

      <div className="search-bar">
        <input className="input search-input" placeholder="Search name, SKU or description…"
          value={filters.q} onChange={e => { setPage(1); setFilters(f => ({ ...f, q: e.target.value })); }} />
        <select className="select" style={{ width: 160 }} value={filters.category}
          onChange={e => { setPage(1); setFilters(f => ({ ...f, category: e.target.value })); }}>
          <option value="">All categories</option>
          {data.categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="select" style={{ width: 170 }} value={filters.warehouse}
          onChange={e => { setPage(1); setFilters(f => ({ ...f, warehouse: e.target.value })); }}>
          <option value="">All warehouses</option>
          {data.warehouses.map(w => <option key={w._id} value={w._id}>{w.name}</option>)}
        </select>
        <select className="select" style={{ width: 150 }} value={filters.low_stock}
          onChange={e => { setPage(1); setFilters(f => ({ ...f, low_stock: e.target.value })); }}>
          <option value="">All stock levels</option>
          <option value="true">Low stock only</option>
        </select>
      </div>

      {loading ? (
        <div className="empty-state"><div className="skeleton skeleton-text" style={{ width: 240 }} /></div>
      ) : data.products.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon" aria-hidden="true">📦</div>
          <div className="empty-state-title">No products yet</div>
          <p className="empty-state-desc">
            Add them one at a time, import a spreadsheet, or push them in from another system
            through the API.
          </p>
          <button className="btn btn-primary btn-sm" onClick={() => setEditing({})}>+ New product</button>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Product</th><th>Category</th><th className="num">Price</th>
                <th>Stock by warehouse</th><th className="num">Total</th><th />
              </tr>
            </thead>
            <tbody>
              {data.products.map(p => {
                const total = (p.stock || []).reduce((sum, s) => sum + (s.quantity || 0), 0);
                const low = (p.stock || []).some(s => (s.quantity || 0) <= (s.reorder_level || 0));
                return (
                  <tr key={p._id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>
                        {p.name}
                        {p.active === false && <span className="badge badge-neutral" style={{ marginLeft: 6 }}>Inactive</span>}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {p.sku}{p.unit ? ` · per ${p.unit}` : ''}
                      </div>
                    </td>
                    <td>{p.category || '—'}</td>
                    <td className="num">{money(p.price, p.currency)}</td>
                    <td>
                      {(p.stock || []).length === 0
                        ? <span style={{ color: 'var(--text-muted)' }}>Not stocked</span>
                        : (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {p.stock.map(s => (
                              <span key={s.warehouse?._id || s.warehouse}
                                className={'badge ' + ((s.quantity || 0) <= (s.reorder_level || 0) ? 'badge-warning' : 'badge-neutral')}
                                title={s.reorder_level ? `Reorder at ${s.reorder_level}` : undefined}>
                                {s.warehouse?.code || '—'} {s.quantity ?? 0}
                              </span>
                            ))}
                          </div>
                        )}
                    </td>
                    <td className={'num' + (low ? ' tabular' : '')} style={low ? { color: 'var(--warning)', fontWeight: 600 } : {}}>
                      {total}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setMoving(p)}
                          disabled={!data.warehouses.length}>Move stock</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditing(p)}>Edit</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => remove(p)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data.pages > 1 && (
        <div className="pagination">
          <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
          <span>Page {page} of {data.pages}</span>
          <button className="btn btn-ghost btn-sm" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}

      {editing && (
        <ProductModal
          product={editing}
          warehouses={data.warehouses}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {importing && (
        <ImportModal
          warehouses={data.warehouses}
          onClose={() => setImporting(false)}
          onImported={() => { setImporting(false); load(); }}
        />
      )}
      {moving && (
        <MoveStockModal
          product={moving}
          warehouses={data.warehouses}
          onClose={() => setMoving(null)}
          onMoved={() => { setMoving(null); load(); }}
        />
      )}
    </div>
  );
}

function ProductModal({ product, warehouses, onClose, onSaved }) {
  const isNew = !product._id;
  const [form, setForm] = useState(() => ({
    sku: product.sku || '',
    name: product.name || '',
    description: product.description || '',
    category: product.category || '',
    unit: product.unit || 'each',
    price: product.price ?? '',
    currency: product.currency || 'USD',
    tax_rate: product.tax_rate ?? '',
    active: product.active !== false,
    stock: warehouses.map(w => {
      const line = (product.stock || []).find(s => String(s.warehouse?._id || s.warehouse) === String(w._id));
      return {
        warehouse: w._id,
        code: w.code,
        name: w.name,
        quantity: line?.quantity ?? '',
        reorder_level: line?.reorder_level ?? '',
        price: line?.price ?? '',
      };
    }),
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const setStock = (i, field, value) => setForm(f => ({
    ...f, stock: f.stock.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)),
  }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        // Only send warehouse rows the user actually filled in, so an untouched
        // warehouse doesn't get a zero-stock row it never had.
        stock: form.stock
          .filter(s => s.quantity !== '' || s.reorder_level !== '' || s.price !== '')
          .map(s => ({
            warehouse: s.warehouse,
            quantity: Number(s.quantity) || 0,
            reorder_level: Number(s.reorder_level) || 0,
            price: s.price === '' ? null : Number(s.price),
          })),
      };
      if (isNew) await api.post('/products', payload);
      else await api.put(`/products/${product._id}`, payload);
      toast.success(isNew ? 'Product added' : 'Product saved');
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 640 }}>
        <div className="modal-title">{isNew ? 'New product' : form.name}</div>

        {error && <div className="alert alert-danger" role="alert">{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
          <div className="form-group">
            <label className="label">SKU <span className="required">*</span></label>
            <input className="input" value={form.sku} onChange={e => set('sku', e.target.value)} placeholder="CEM-32" />
            <span className="field-help">Your own code. Imports and the API match on this.</span>
          </div>
          <div className="form-group">
            <label className="label">Name <span className="required">*</span></label>
            <input className="input" value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="Cement 32.5N 50kg" />
          </div>
        </div>

        <div className="form-group">
          <label className="label">Description <span className="optional">(optional)</span></label>
          <textarea className="textarea" style={{ minHeight: 60 }} value={form.description}
            onChange={e => set('description', e.target.value)} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label className="label">Category</label>
            <input className="input" value={form.category} onChange={e => set('category', e.target.value)} placeholder="Building" />
          </div>
          <div className="form-group">
            <label className="label">Sold per</label>
            <input className="input" value={form.unit} onChange={e => set('unit', e.target.value)} placeholder="bag" />
          </div>
          <div className="form-group">
            <label className="label">Currency</label>
            <input className="input" value={form.currency} onChange={e => set('currency', e.target.value.toUpperCase())} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label className="label">Price <span className="required">*</span></label>
            <input className="input" type="number" step="0.01" min="0" value={form.price}
              onChange={e => set('price', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="label">Tax %</label>
            <input className="input" type="number" step="0.01" min="0" max="100" value={form.tax_rate}
              onChange={e => set('tax_rate', e.target.value)} placeholder="workspace default" />
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 18 }}>
            <input type="checkbox" id="prod-active" checked={form.active} onChange={e => set('active', e.target.checked)} />
            <label htmlFor="prod-active" style={{ fontSize: 13 }}>Available to quote</label>
          </div>
        </div>

        {form.stock.length > 0 && (
          <>
            <div className="label" style={{ marginTop: 4 }}>Stock by warehouse</div>
            <p className="field-help" style={{ marginBottom: 8 }}>
              Leave a row blank to keep this product unstocked at that warehouse. A price here
              overrides the one above for quotes from that branch.
            </p>
            <div className="table-wrap" style={{ marginBottom: 16 }}>
              <table>
                <thead>
                  <tr><th>Warehouse</th><th className="num">Quantity</th><th className="num">Reorder at</th><th className="num">Price override</th></tr>
                </thead>
                <tbody>
                  {form.stock.map((s, i) => (
                    <tr key={s.warehouse}>
                      <td>{s.name} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{s.code}</span></td>
                      <td><input className="input" type="number" style={{ width: 90 }} value={s.quantity}
                        onChange={e => setStock(i, 'quantity', e.target.value)} /></td>
                      <td><input className="input" type="number" style={{ width: 90 }} value={s.reorder_level}
                        onChange={e => setStock(i, 'reorder_level', e.target.value)} /></td>
                      <td><input className="input" type="number" step="0.01" style={{ width: 100 }} value={s.price}
                        placeholder="—" onChange={e => setStock(i, 'price', e.target.value)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className={'btn btn-primary' + (saving ? ' is-loading' : '')} onClick={save} disabled={saving}>
            {saving ? 'Saving…' : (isNew ? 'Add product' : 'Save changes')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Minimal CSV reader — same approach as the contacts import: parse in the
// browser, send rows as JSON, let the server report per-row problems.
function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''));

  return lines.slice(1).map(line => {
    const values = [];
    let current = '', inQuote = false;
    for (const ch of line) {
      if (ch === '"') inQuote = !inQuote;
      else if (ch === ',' && !inQuote) { values.push(current.trim()); current = ''; }
      else current += ch;
    }
    values.push(current.trim());

    const row = {};
    headers.forEach((h, i) => { row[h] = (values[i] || '').replace(/^["']|["']$/g, ''); });
    return row;
  }).filter(row => Object.values(row).some(Boolean));
}

function ImportModal({ warehouses, onClose, onImported }) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [stockMode, setStockMode] = useState('set');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setResult(null);
    const reader = new FileReader();
    reader.onload = ev => {
      const parsed = parseCsv(ev.target.result);
      if (!parsed.length) {
        setError('No rows found. The file needs a header row including at least "sku".');
        return;
      }
      if (!parsed[0].sku && parsed[0].sku !== '') {
        setError('No "sku" column found — that\'s the column we match on.');
        return;
      }
      setRows(parsed);
      setFileName(file.name);
    };
    reader.readAsText(file);
  };

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post('/products/import', { rows, stock_mode: stockMode });
      setResult(r.data);
      toast.success(`${r.data.created} added, ${r.data.updated} updated`);
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 620 }}>
        <div className="modal-title">Import products from a spreadsheet</div>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: -8, marginBottom: 14 }}>
          Save your sheet as CSV. Columns: <code>sku, name, description, category, unit, price,
          currency, tax_rate, quantity, warehouse_code, active</code>. Rows are matched on
          <strong> sku</strong> — an existing product is updated, a new one is created.
        </p>

        {error && <div className="alert alert-danger" role="alert">{error}</div>}

        <div onClick={() => fileRef.current.click()}
          style={{
            border: '2px dashed var(--border-strong)', borderRadius: 10, padding: '22px 16px',
            textAlign: 'center', cursor: 'pointer', color: 'var(--text-muted)', marginBottom: 14,
          }}>
          {fileName
            ? <><strong style={{ color: 'var(--text)' }}>{fileName}</strong><br />
              <span style={{ fontSize: 12 }}>{rows.length} rows — click to choose another file</span></>
            : <>Click to choose a CSV file<br /><span style={{ fontSize: 12 }}>up to 5000 rows at a time</span></>}
        </div>
        <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={onFile} />

        <div className="form-group">
          <label className="label">Quantities in this file</label>
          <select className="select" value={stockMode} onChange={e => setStockMode(e.target.value)}>
            <option value="set">Replace the stock figure (a stock take)</option>
            <option value="add">Add to what's already there (a delivery)</option>
          </select>
          <span className="field-help">
            Only warehouses named in the file are touched — the others keep their figures.
          </span>
        </div>

        {warehouses.length === 0 && (
          <div className="alert alert-warning">
            No warehouses yet, so rows carrying a quantity will be skipped. Add a warehouse first.
          </div>
        )}

        {rows.length > 0 && !result && (
          <div className="table-wrap" style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 12 }}>
            <table>
              <thead><tr><th>SKU</th><th>Name</th><th className="num">Price</th><th className="num">Qty</th><th>Warehouse</th></tr></thead>
              <tbody>
                {rows.slice(0, 8).map((r, i) => (
                  <tr key={i}>
                    <td>{r.sku || <span style={{ color: 'var(--danger)' }}>missing</span>}</td>
                    <td>{r.name || '—'}</td>
                    <td className="num">{r.price || '—'}</td>
                    <td className="num">{r.quantity || r.stock || '—'}</td>
                    <td>{r.warehouse_code || r.warehouse || 'default'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {result && (
          <div className={'alert ' + (result.skipped ? 'alert-warning' : 'alert-success')}>
            <div>
              <div className="alert-title">
                {result.created} added · {result.updated} updated · {result.skipped} skipped
              </div>
              {result.errors?.length > 0 && (
                <ul style={{ margin: '6px 0 0 16px', fontSize: 12 }}>
                  {result.errors.slice(0, 8).map((e, i) => (
                    <li key={i}>Row {e.row}{e.sku ? ` (${e.sku})` : ''}: {e.error}</li>
                  ))}
                  {result.errors.length > 8 && <li>…and {result.errors.length - 8} more</li>}
                </ul>
              )}
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={result ? onImported : onClose}>
            {result ? 'Done' : 'Cancel'}
          </button>
          {!result && (
            <button className={'btn btn-primary' + (busy ? ' is-loading' : '')} onClick={run} disabled={busy || !rows.length}>
              {busy ? 'Importing…' : `Import ${rows.length} row${rows.length === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MoveStockModal({ product, warehouses, onClose, onMoved }) {
  const [form, setForm] = useState({ from: '', to: '', quantity: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const held = (warehouseId) => {
    const line = (product.stock || []).find(s => String(s.warehouse?._id || s.warehouse) === String(warehouseId));
    return line?.quantity || 0;
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/products/stock/move', {
        product_id: product._id,
        from: form.from || undefined,
        to: form.to || undefined,
        quantity: Number(form.quantity),
      });
      toast.success('Stock updated');
      onMoved();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not move the stock');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-title">Move stock — {product.name}</div>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: -8, marginBottom: 14 }}>
          Leave <strong>From</strong> empty for a delivery in, or <strong>To</strong> empty for a
          write-off. Fill both to transfer between branches.
        </p>

        {error && <div className="alert alert-danger" role="alert">{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label className="label">From</label>
            <select className="select" value={form.from} onChange={e => setForm(f => ({ ...f, from: e.target.value }))}>
              <option value="">— nowhere (stock in) —</option>
              {warehouses.map(w => (
                <option key={w._id} value={w._id}>{w.name} ({held(w._id)})</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="label">To</label>
            <select className="select" value={form.to} onChange={e => setForm(f => ({ ...f, to: e.target.value }))}>
              <option value="">— nowhere (write off) —</option>
              {warehouses.map(w => (
                <option key={w._id} value={w._id}>{w.name} ({held(w._id)})</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="label">Quantity</label>
          <input className="input" type="number" min="1" value={form.quantity}
            onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} />
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className={'btn btn-primary' + (busy ? ' is-loading' : '')} onClick={submit}
            disabled={busy || !form.quantity || (!form.from && !form.to)}>
            {busy ? 'Moving…' : 'Move stock'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── warehouses ───────────────────────────────────────────────────────────────

function WarehousesTab({ isOwner }) {
  const [warehouses, setWarehouses] = useState([]);
  const [form, setForm] = useState({ name: '', code: '', address: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/warehouses');
      setWarehouses(r.data);
    } catch (err) { toast.error(err.response?.data?.error || 'Could not load warehouses'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/warehouses', form);
      setForm({ name: '', code: '', address: '', phone: '' });
      toast.success('Warehouse added');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add the warehouse');
    } finally { setBusy(false); }
  };

  const makeDefault = async (w) => {
    try {
      await api.put(`/warehouses/${w._id}`, { is_default: true });
      toast.success(`${w.name} is now the default`);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete ${w.name}?`)) return;
    try {
      await api.delete(`/warehouses/${w._id}`);
      toast.success('Warehouse deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed', { duration: 6000 }); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="card" style={{ padding: 20 }}>
        <div className="card-title">Where your stock sits</div>
        <p className="card-desc">
          Quotations are raised against one warehouse, so a customer can be quoted from the branch
          that will actually supply them. The default is used when nothing else is chosen.
        </p>

        {warehouses.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon" aria-hidden="true">🏬</div>
            <div className="empty-state-title">No warehouses yet</div>
            <p className="empty-state-desc">
              Add one for each place you hold stock. A single-branch business only needs one.
            </p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Warehouse</th><th>Code</th><th>Address</th>
                  <th className="num">SKUs</th><th className="num">Units</th><th className="num">Stock value</th><th />
                </tr>
              </thead>
              <tbody>
                {warehouses.map(w => (
                  <tr key={w._id}>
                    <td>
                      {w.name}
                      {w.is_default && <span className="badge badge-brand" style={{ marginLeft: 6 }}>Default</span>}
                      {w.active === false && <span className="badge badge-neutral" style={{ marginLeft: 6 }}>Inactive</span>}
                    </td>
                    <td><code>{w.code}</code></td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{w.address || '—'}</td>
                    <td className="num">{w.stock_summary?.skus ?? 0}</td>
                    <td className="num">{w.stock_summary?.units ?? 0}</td>
                    <td className="num">{money(w.stock_summary?.value)}</td>
                    <td>
                      {isOwner && (
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          {!w.is_default && (
                            <button className="btn btn-ghost btn-sm" onClick={() => makeDefault(w)}>Make default</button>
                          )}
                          <button className="btn btn-ghost btn-sm" onClick={() => remove(w)}>Delete</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isOwner && (
        <div className="card" style={{ padding: 20 }}>
          <div className="card-title">Add a warehouse</div>
          {error && <div className="alert alert-danger" role="alert">{error}</div>}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="label">Name <span className="required">*</span></label>
              <input className="input" style={{ width: 180 }} value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Harare branch" />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="label">Code <span className="required">*</span></label>
              <input className="input" style={{ width: 100 }} value={form.code}
                onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="HRE" />
              <span className="field-help">Used in imports and the API.</span>
            </div>
            <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 200 }}>
              <label className="label">Address <span className="optional">(optional)</span></label>
              <input className="input" value={form.address}
                onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
            </div>
            <button className={'btn btn-primary' + (busy ? ' is-loading' : '')} onClick={create}
              disabled={busy || !form.name.trim() || !form.code.trim()}>
              {busy ? 'Adding…' : 'Add warehouse'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
